import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "components/ui/alert";
import { usePostHog } from "@posthog/react";
import { SupportButton } from "components/snackbars/SupportButton";
import {
  User,
  getActiveOrganisation,
  useIdentity,
} from "contexts/identity-context";
import * as QueryKeys from "data";
import {
  fetchApiKeys,
  fetchApiProducts,
  fetchCatalogueList,
  fetchCredits,
  fetchDefinitions,
  fetchDemos,
  fetchOrganisation,
  fetchPaymentMethod,
  fetchPermissions,
  fetchSubscription,
  fetchUserStatus,
} from "data/queries-common";
import {
  ChevronLeft,
  ChevronRight,
  LifeBuoy,
  Newspaper,
  OctagonAlert,
  Scale,
} from "lucide-react";
import queryString from "query-string";
import * as React from "react";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useHistory, useLocation } from "react-router";
import { RouteSwitch } from "routes/route-switch";
import { resolveIdentityId } from "utils/identity";
import { clearRedirectInfo, getRedirectUrl } from "utils/redirect-manager";
import { Intercom } from "./boot-intercom";

import { AppSidebar } from "@/components/side-bar/app-sidebar";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";

export const SignedIn = () => {
  const [identityState] = useIdentity();

  const org = getActiveOrganisation(identityState);
  const identityId = resolveIdentityId(identityState);
  const posthog = usePostHog();

  useEffect(() => {
    const user = identityState as User;
    posthog?.identify(user.cognitoSub, {
      email: user.email,
    });

    if (org) {
      posthog.group("organisation", org.organisationId, { orgName: org.name });
    }
  }, [identityState]);

  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.definitionsKey],
      queryFn: () => fetchDefinitions(),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.apiKeys, identityId],
      queryFn: () => fetchApiKeys(),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.apiProducts],
      queryFn: () => fetchApiProducts(),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.subscription, identityId],
      queryFn: () => fetchSubscription(),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.paymentMethod, identityId],
      queryFn: () => fetchPaymentMethod(),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.credits, identityId],
      queryFn: () => fetchCredits(),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.permissions, identityId],
      queryFn: () => fetchPermissions(),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.organisation, identityId],
      queryFn: () => fetchOrganisation(),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.demosKey, identityId],
      queryFn: () => fetchDemos(),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.catalogueListKey, identityId, ""],
      queryFn: () => fetchCatalogueList(""),
    });
    queryClient.prefetchQuery({
      queryKey: [QueryKeys.permissions, identityId, null, []],
      queryFn: () => fetchPermissions(),
    });
  }, [identityId]);

  /**
   * App in a signed out state.
   * The "Geo-Sidebar" with our menu items on the left.
   * On the right, a paper with the context for whatever route we're on
   */

  const userStatusQuery = useQuery({
    queryKey: [QueryKeys.userStatus, identityId],
    queryFn: () => fetchUserStatus(),
  });

  const isActive = userStatusQuery.data
    ? userStatusQuery.data.status === "active"
    : true;

  return (
    <>
      <Intercom />
      <div className="block h-screen w-screen overflow-hidden">
        {isActive ? (
          <MainLayout />
        ) : (
          <div className="flex h-screen w-screen items-center justify-center">
            <Alert variant="destructive">
              <OctagonAlert className="size-4" />
              <AlertTitle>Your Account Has Been Disabled</AlertTitle>
              <AlertDescription>
                If you believe this is an error or need assistance, please click
                the 'Contact Us' button, or email support@geoscape.com.au to get
                in touch with our support team. We're here to help!
              </AlertDescription>
              <AlertAction>
                <SupportButton />
              </AlertAction>
            </Alert>
          </div>
        )}
      </div>
    </>
  );
};

export default function MainLayout() {
  const history = useHistory();

  useEffect(() => {
    // Check for saved redirect info (from signup/verification flow)
    const savedRedirectUrl = getRedirectUrl();
    if (savedRedirectUrl) {
      history.push(savedRedirectUrl);
      clearRedirectInfo();
    }
  }, []);

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <RouteSwitch />
      </SidebarInset>
    </SidebarProvider>
  );
}
